/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from "react";
import React, { Component } from "react";
import { useTranslation } from "react-i18next";
import { logger } from "@/lib/logger";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// Helper component to use useTranslation hook inside class component
const ErrorDisplay = () => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">
          {t("errorBoundary.title")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t("errorBoundary.description")}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
        >
          {t("errorBoundary.refresh")}
        </button>
      </div>
    </div>
  );
};

export class RealtimeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a WebSocket/realtime related error
    if (
      error.message?.includes("WebSocket") ||
      error.message?.includes("insecure") ||
      error.name === "SecurityError"
    ) {
      logger.warn(
        "Realtime connection failed, continuing without realtime features:",
        error
      );
      // Don't treat realtime failures as fatal errors
      return { hasError: false };
    }

    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    if (
      error.message?.includes("WebSocket") ||
      error.message?.includes("insecure") ||
      error.name === "SecurityError"
    ) {
      logger.warn("Caught realtime error, app will continue normally:", error);
      return;
    }

    logger.error("Unexpected error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorDisplay />;
    }

    return this.props.children;
  }
}
